import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import TranslateIcon from '@mui/icons-material/Translate';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import MovieIcon from '@mui/icons-material/Movie';
import { useQueryClient } from 'react-query';

import { VpCaptionBadge } from '@vp-ui/components/VpCaptionBadge';
import { VpVideoCaptionPlayer } from '@vp-ui/components/VpVideoCaptionPlayer';
import { VpCaptionEditor } from '@vp-ui/components/VpCaptionEditor';
import {
  useCaptionPolling,
  useGenerateCaption,
  useTranslateCaption,
  useStartBurnIn,
  CAPTIONS_QUERY_KEY
} from '@src/hooks/react-query/useCaptionsData';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import {
  CAPTION_PRESETS,
  CAPTION_PRESET_KEYS,
  DEFAULT_PRESET,
  HIGHLIGHT_MODES,
  DEFAULT_HIGHLIGHT_MODE,
  WORDS_PER_DISPLAY,
  DEFAULT_WORDS_PER_DISPLAY,
  CROP_PRESETS,
  DEFAULT_CROP
} from '@src/constants/caption-presets';
import { CAPTION_LANGUAGES } from '@src/constants/caption-languages';

const boxStyles = { mb: 4 };

export const VpMediaCaptionInput = ({ media }) => {
  const [playerOpen, setPlayerOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(DEFAULT_PRESET);
  const [highlightMode, setHighlightMode] = useState(DEFAULT_HIGHLIGHT_MODE);
  const [wordsPerDisplay, setWordsPerDisplay] = useState(DEFAULT_WORDS_PER_DISPLAY);
  const [translateLang, setTranslateLang] = useState('');
  const [selectedCaptionId, setSelectedCaptionId] = useState(null);
  const [selectedCrop, setSelectedCrop] = useState(DEFAULT_CROP);
  const mediaId = media?.mediaId;
  const queryClient = useQueryClient();

  const { data: captionsResponse, isLoading } = useCaptionPolling(mediaId);
  const { mutate: generateCaption, isLoading: isGenerating } = useGenerateCaption();
  const { mutate: translateCaptionMutate, isLoading: isTranslating } = useTranslateCaption(mediaId);
  const { mutate: startBurnInMutate, isLoading: isBurningIn } = useStartBurnIn(mediaId);
  const { addSnackbarError, addSnackbarInfo } = useVpSnackbar();

  const captions = Array.isArray(captionsResponse) ? captionsResponse : [];
  const latestCaption = captions[0] || null;
  const originalCaption = captions.find((c) => !c.isTranslation && c.status === 'completed');
  const hasCompletedCaption = captions.some((c) => c.status === 'completed');
  const isProcessing = captions.some((c) => c.status === 'pending' || c.status === 'processing');

  const existingLanguages = captions
    .filter((c) => c.status === 'completed' || c.status === 'processing')
    .map((c) => c.targetLanguage);

  const availableLanguages = CAPTION_LANGUAGES.filter(
    (lang) => !existingLanguages.includes(lang.code)
  );

  const activeCaptionForPlayer = selectedCaptionId
    ? captions.find((c) => c.id === selectedCaptionId) || latestCaption
    : latestCaption;

  const handleGenerateCaption = () => {
    generateCaption(
      { mediaId },
      {
        onSuccess: () => addSnackbarInfo({ message: 'Generating captions. This usually takes 1–3 minutes.' }),
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  const handleTranslate = () => {
    if (!translateLang || !originalCaption) return;
    const langLabel = CAPTION_LANGUAGES.find((l) => l.code === translateLang)?.label || translateLang;
    translateCaptionMutate(
      { captionId: originalCaption.id, targetLanguage: translateLang },
      {
        onSuccess: () => {
          addSnackbarInfo({ message: `Translating to ${langLabel}. This may take a few minutes.` });
          setTranslateLang('');
        },
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  const handleBurnIn = () => {
    if (!activeCaptionForPlayer) return;
    startBurnInMutate(
      {
        captionId: activeCaptionForPlayer.id,
        preset: selectedPreset,
        highlightMode,
        wordsPerDisplay: highlightMode === HIGHLIGHT_MODES.WORD_BY_WORD ? wordsPerDisplay : undefined,
        crop: selectedCrop
      },
      {
        onSuccess: () => addSnackbarInfo({ message: 'Video processing. This may take a few minutes.' }),
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  if (isLoading) {
    return (
      <Box sx={boxStyles}>
        <Typography variant="inputLabel">Captions</Typography>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={boxStyles}>
      <Stack direction="row" gap="8px" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="inputLabel">Captions</Typography>
        {latestCaption && <VpCaptionBadge status={latestCaption.status} />}
      </Stack>

      {!latestCaption && !isProcessing && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleGenerateCaption}
          disabled={isGenerating}
          startIcon={isGenerating ? <CircularProgress size={16} /> : null}
        >
          {isGenerating ? 'Starting...' : 'Add Captions'}
        </Button>
      )}

      {isProcessing && !hasCompletedCaption && (
        <Typography variant="body2" color="text.secondary">
          Generating captions... This usually takes 1-3 minutes.
        </Typography>
      )}

      {hasCompletedCaption && (
        <Stack gap="12px">
          <Stack direction="row" gap="8px" flexWrap="wrap" alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                // Ensure fresh caption data (including new vttUrl) before opening the player
                queryClient.invalidateQueries([CAPTIONS_QUERY_KEY, mediaId]);
                setPlayerOpen(true);
              }}
            >
              Watch with Captions
            </Button>
            {latestCaption?.vttUrl && (
              <Button variant="outlined" size="small" component="a" href={latestCaption.vttUrl} target="_blank" rel="noopener noreferrer">
                Download VTT
              </Button>
            )}
            {latestCaption?.srtUrl && (
              <Button variant="outlined" size="small" component="a" href={latestCaption.srtUrl} target="_blank" rel="noopener noreferrer">
                Download SRT
              </Button>
            )}
            {activeCaptionForPlayer?.status === 'completed' && (
              <Button variant="outlined" size="small" onClick={() => setEditorOpen(true)} startIcon={<EditIcon />}>
                Edit
              </Button>
            )}
          </Stack>

          {captions.filter((c) => c.status === 'completed').length > 1 && (
            <Stack gap="4px">
              <Typography variant="body2" color="text.secondary">Available Languages</Typography>
              <Stack direction="row" gap="6px" flexWrap="wrap">
                {captions
                  .filter((c) => c.status === 'completed')
                  .map((c) => {
                    const lang = CAPTION_LANGUAGES.find((l) => l.code === c.targetLanguage);
                    return (
                      <Chip
                        key={c.id}
                        label={lang?.label || c.targetLanguage}
                        variant={activeCaptionForPlayer?.id === c.id ? 'filled' : 'outlined'}
                        color={activeCaptionForPlayer?.id === c.id ? 'primary' : 'default'}
                        onClick={() => setSelectedCaptionId(c.id)}
                        size="small"
                      />
                    );
                  })}
              </Stack>
            </Stack>
          )}

          {originalCaption && availableLanguages.length > 0 && (
            <Stack direction="row" gap="8px" alignItems="center">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={translateLang} onChange={(e) => setTranslateLang(e.target.value)} displayEmpty size="small">
                  <MenuItem value="" disabled>Translate to...</MenuItem>
                  {availableLanguages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>{lang.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={handleTranslate}
                disabled={!translateLang || isTranslating}
                startIcon={isTranslating ? <CircularProgress size={16} /> : <TranslateIcon />}
              >
                {isTranslating ? 'Translating...' : 'Translate'}
              </Button>
            </Stack>
          )}

          <Stack gap="8px">
            <Typography variant="body2" color="text.secondary">Highlight Mode</Typography>
            <ToggleButtonGroup value={highlightMode} exclusive onChange={(_, value) => value && setHighlightMode(value)} size="small" aria-label="Highlight mode">
              <ToggleButton value={HIGHLIGHT_MODES.KARAOKE}>Karaoke</ToggleButton>
              <ToggleButton value={HIGHLIGHT_MODES.WORD_BY_WORD}>Word by Word</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {highlightMode === HIGHLIGHT_MODES.WORD_BY_WORD && (
            <Stack gap="8px">
              <Typography variant="body2" color="text.secondary">Words per Display</Typography>
              <ToggleButtonGroup value={wordsPerDisplay} exclusive onChange={(_, value) => value && setWordsPerDisplay(value)} size="small" aria-label="Words per display">
                <ToggleButton value={WORDS_PER_DISPLAY.ONE}>1 Word</ToggleButton>
                <ToggleButton value={WORDS_PER_DISPLAY.THREE}>3 Words</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          <Stack gap="8px">
            <Typography variant="body2" color="text.secondary">Caption Style</Typography>
            <Stack direction="row" gap="6px" flexWrap="wrap">
              {CAPTION_PRESET_KEYS.map((key) => (
                <Chip
                  key={key}
                  label={CAPTION_PRESETS[key].label}
                  variant={selectedPreset === key ? 'filled' : 'outlined'}
                  color={selectedPreset === key ? 'primary' : 'default'}
                  onClick={() => setSelectedPreset(key)}
                  size="small"
                />
              ))}
            </Stack>
          </Stack>

          <Divider />

          <Stack gap="8px">
            <Typography variant="body2" fontWeight={600}>Export Video with Captions</Typography>

            <Stack gap="8px">
              <Typography variant="body2" color="text.secondary">Aspect Ratio</Typography>
              <ToggleButtonGroup value={selectedCrop} exclusive onChange={(_, value) => value && setSelectedCrop(value)} size="small" aria-label="Aspect ratio">
                {CROP_PRESETS.map((crop) => (
                  <ToggleButton key={crop.value} value={crop.value}>{crop.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>

            <Stack direction="row" gap="8px" alignItems="center">
              <Button
                variant="contained"
                size="small"
                onClick={handleBurnIn}
                disabled={isBurningIn || activeCaptionForPlayer?.burnInStatus === 'processing'}
                startIcon={isBurningIn || activeCaptionForPlayer?.burnInStatus === 'processing' ? <CircularProgress size={16} /> : <MovieIcon />}
              >
                {isBurningIn || activeCaptionForPlayer?.burnInStatus === 'processing' ? 'Exporting...' : 'Export Video'}
              </Button>

              {activeCaptionForPlayer?.burnInStatus === 'completed' && activeCaptionForPlayer?.burnInUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href={activeCaptionForPlayer.burnInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<DownloadIcon />}
                >
                  Download Video
                </Button>
              )}

              {activeCaptionForPlayer?.burnInStatus === 'failed' && (
                <Typography variant="body2" color="error">Export failed. Try again.</Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      )}

      {latestCaption?.status === 'failed' && (
        <Stack gap="8px">
          <Typography variant="body2" color="error">Caption generation failed. Please try again.</Typography>
          <Button variant="outlined" size="small" onClick={handleGenerateCaption} disabled={isGenerating}>Retry</Button>
        </Stack>
      )}

      <Dialog open={playerOpen} onClose={() => setPlayerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Video with Captions
            <IconButton onClick={() => setPlayerOpen(false)} size="small"><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {playerOpen && (
            <VpVideoCaptionPlayer
              mediaUrl={media?.mediaUrl}
              captions={activeCaptionForPlayer ? [activeCaptionForPlayer] : captions}
              thumbnailUrl={media?.thumbnailUrl}
              preset={selectedPreset}
              highlightMode={highlightMode}
              wordsPerDisplay={wordsPerDisplay}
            />
          )}
        </DialogContent>
      </Dialog>

      <VpCaptionEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        captionId={activeCaptionForPlayer?.id}
        mediaId={mediaId}
      />
    </Box>
  );
};

VpMediaCaptionInput.propTypes = {
  media: PropTypes.shape({
    mediaId: PropTypes.string,
    mediaUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string
  })
};

export default VpMediaCaptionInput;



// WEBPACK FOOTER //
// ./src/vp-ui/form-fields/media-details/VpMediaCaptionInput.jsx